import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  resource,
} from '@angular/core';
import { AdminGamesService } from '@features/admin/admin-games/admin-games.service';
import { Skin } from '@core/interfaces/skin.interface';
import { CostComponent } from '@core/components/cost/cost.component';
import { SkinsListComponent } from '@core/components/skins-list/skins-list.component';
import { ClipboardService } from '@core/services/clipboard.service';
import { AdminGameOrTransactionSkins } from '@features/admin/admin-games/admin-games.interface';
import { assignColorToSkin } from '@core/utils/skins.util';
import { v4 as uuidv4 } from 'uuid';
import { sleep } from '@core/utils/sleep.util';
import { ModalService } from '@core/modals/services/modal.service';
import { RakeType } from '@core/types/rake.type';

@Component({
  selector: 'app-admin-game-skins-modal',
  imports: [SkinsListComponent, CostComponent],
  templateUrl: './admin-game-skins-modal.component.html',
  styleUrl: './admin-game-skins-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    class: 'modal-container flex flex-col',
  },
})
export class AdminGameSkinsModalComponent {
  public readonly adminGamesService = inject(AdminGamesService);
  public readonly clipboardService = inject(ClipboardService);
  public readonly modalService = inject(ModalService);
  type = input.required<string>();
  isShowStorage = input<boolean>(false);
  rake = input<RakeType>();
  gameid = input.required<number>();
  roundid = input<number>();
  modalType = input<'show' | 'sent'>();
  skins = input<AdminGameOrTransactionSkins>();

  adminGameSkins = resource({
    params: () => {
      const gameid = this.gameid();
      const type = this.type();
      const skins = this.skins();
      const roundid = this.roundid();
      const modalType = this.modalType() ?? 'show';

      return {
        gameid,
        type,
        skins,
        modalType,
        roundid,
      };
    },
    loader: async (params) => {
      await sleep(500);

      const skins = params.params.skins;
      const modalType = params.params.modalType;
      const isSkinsEmpty = (skins?.length ?? 0) > 0;

      if (isSkinsEmpty) {
        return this.mapAdminGameSkinsToSkin(skins);
      }
      const response =
        modalType === 'show'
          ? await this.adminGamesService.loadGameSkins(
              params.params.type,
              params.params.gameid,
              params.params.roundid
            )
          : await this.adminGamesService.loadSentSkins(
              params.params.type,
              params.params.gameid,
              params.params.roundid
            );
      return this.mapAdminGameSkinsToSkin(response);
    },
  });

  public totalValue = computed(() => {
    return this.adminGameSkins
      .value()
      ?.reduce((acc, skin) => acc + skin.price, 0);
  });

  private mapAdminGameSkinsToSkin(skins: AdminGameOrTransactionSkins): Skin[] {
    const data = skins ?? [];
    return data
      .map((skin) => {
        const isShowStorage = this.isShowStorage();
        if (!isShowStorage && 'is_storage' in skin) {
          delete skin.is_storage;
        }
        return {
          ...skin,
          image: skin?.image,
          name: skin?.name,
          price: skin?.price,
          assetid: uuidv4(),
        } as Skin;
      })
      .map((skin) => assignColorToSkin(skin));
  }

  public handleOpenAdminUserProfile(user: Skin['user']) {
    this.modalService.openModal('AdminUserModal', {
      user,
      hidePrimaryData: true,
    });
  }
}
